import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { formatPct, formatNumber } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CHART_COLORS } from '@/constants/providers';

export default function CompetitorsTab({ project }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/projects/${project.id}/analytics/competitors`);
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } finally {
        setLoading(false);
      }
    })();
  }, [project.id]);

  if (loading) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-64" />)}</div>;

  const positive = items.filter((c) => c.count > 0);
  const brand = items.find((c) => c.is_brand);

  return (
    <div className="space-y-4">
      {items.length === 0 || total === 0 ? (
        <ChartCard title="Competitors" subtitle="How your brand ranks in AI answers">
          <EmptyState title="No data yet" description="Add competitors to your project and run a scan." />
        </ChartCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Share of voice" subtitle={`Total mentions: ${formatNumber(total)}`}>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={positive} dataKey="count" nameKey="name" outerRadius={100} innerRadius={60} paddingAngle={2}>
                    {positive.map((entry, idx) => (
                      <Cell key={entry.name} fill={entry.is_brand ? CHART_COLORS[0] : CHART_COLORS[(idx + 1) % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Competitor breakdown" subtitle={`Your brand: ${brand ? formatPct(brand.share) : '—'}`}>
            <div className="space-y-2">
              {items.map((c) => (
                <div key={c.name} className="rounded-lg border p-3" data-testid={`competitor-row-${c.name}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{c.name}</div>
                      {c.is_brand && <Badge className="text-[10px]">Your brand</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground tabular">{formatNumber(c.count)} mentions</div>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                    <div
                      className={`h-full ${c.is_brand ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--muted-foreground))]/60'}`}
                      style={{ width: `${Math.round(c.share * 100)}%` }}
                    />
                  </div>
                  <div className="mt-1 text-right text-[11px] tabular text-muted-foreground">{formatPct(c.share)}</div>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
